import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Tooltip from "@material-ui/core/Tooltip";
import { withStyles } from "@material-ui/core/styles";

const COPIED_STATUS_DURATION_MS = 3000;

const PromptTooltip = withStyles({
  tooltip: {
    textAlign: 'left',
    fontSize: '14px',
    whiteSpace: 'pre-wrap',
    maxWidth: 'min(36rem, calc(100vw - 32px))',
    maxHeight: '60vh',
    overflowY: 'auto',
  },
})(Tooltip);

const Wrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  margin-top: 1rem;
  font-family: sans-serif;
`;

const ActionButton = styled.button`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #2f80ed;
  border-radius: 0.5rem;
  padding: 0.65rem 0.8rem;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.35;
  cursor: pointer;

  &:hover {
    background: #dbeafe;
  }

  &:focus-visible {
    outline: 3px solid #93c5fd;
    outline-offset: 2px;
  }
`;

const Status = styled.div`
  min-height: 1.25rem;
  margin-top: 0.45rem;
  color: #166534;
  font-size: 0.8rem;
  font-weight: 600;
`;

const CopiedStatus = styled.span`
  display: block;
  text-align: center;
`;

const ManualPrompt = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 9rem;
  margin-top: 0.4rem;
  padding: 0.65rem;
  border: 1px solid #9ca3af;
  border-radius: 0.4rem;
  background: #ffffff;
  color: #1f2937;
  font-size: 0.75rem;
  line-height: 1.4;
  resize: vertical;
`;

const AiPromptAction = ({ label, prompt }) => {
  const [mode, setMode] = useState('idle');
  const [tooltipDismissed, setTooltipDismissed] = useState(false);
  const copiedStatusTimer = useRef(null);
  const latestCopyAttempt = useRef(0);

  useEffect(() => () => {
    latestCopyAttempt.current += 1;
    window.clearTimeout(copiedStatusTimer.current);
  }, []);

  const copyPrompt = async () => {
    setTooltipDismissed(true);
    const copyAttempt = ++latestCopyAttempt.current;

    try {
      if (!navigator.clipboard || !navigator.clipboard.writeText) {
        throw new Error('Clipboard API unavailable');
      }
      await navigator.clipboard.writeText(prompt);
      if (copyAttempt !== latestCopyAttempt.current) {
        return;
      }
      window.clearTimeout(copiedStatusTimer.current);
      setMode('copied');
      copiedStatusTimer.current = window.setTimeout(() => {
        copiedStatusTimer.current = null;
        setMode('idle');
      }, COPIED_STATUS_DURATION_MS);
    } catch (_error) {
      if (copyAttempt !== latestCopyAttempt.current) {
        return;
      }
      window.clearTimeout(copiedStatusTimer.current);
      copiedStatusTimer.current = null;
      setMode('manual');
    }
  };

  return (
    <Wrapper>
      <PromptTooltip
        title={tooltipDismissed ? '' : prompt}
        placement="bottom-start"
        interactive
      >
        <ActionButton
          type="button"
          onClick={copyPrompt}
          onMouseLeave={() => setTooltipDismissed(false)}
          onBlur={() => setTooltipDismissed(false)}
        >
          {label}
        </ActionButton>
      </PromptTooltip>
      <Status role="status" aria-live="polite">
        {mode === 'copied' && (
          <CopiedStatus>Copied — paste into your AI</CopiedStatus>
        )}
        {mode === 'manual' && 'Copy this prompt manually'}
      </Status>
      {mode === 'manual' && (
        <ManualPrompt
          aria-label="Copy this prompt manually"
          readOnly
          value={prompt}
          onFocus={(event) => event.currentTarget.select()}
        />
      )}
    </Wrapper>
  );
};

export default AiPromptAction;
